"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"
import { LanguageSelector } from "@/components/language-selector"
import { Menu, X } from "lucide-react"
import { useLanguage } from "@/contexts/language-context"
import { Logo } from "@/components/logo"
import { AuthButton } from "@/components/auth"
import { SmartAnchorLink } from "@/components/smart-anchor-link"

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { t, currentLanguage } = useLanguage()

  // 根据当前语言决定Logo尺寸
  const logoWidth = currentLanguage === "zh-cn" || currentLanguage === "zh-tw" ? 100 : 100;

  return (
    <header className="fixed top-0 z-50 w-full bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-b border-slate-200/80 dark:border-zinc-700/80 shadow-sm dark:shadow-zinc-900/20">
      <div className="container mx-auto flex h-16 items-center justify-between px-2 sm:px-4 lg:px-6">
        {/* Logo Section with Mobile Menu Button */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          <Link href="/" className="flex items-center">
            <Logo width={logoWidth} height={46} />
          </Link>
          
          {/* Mobile Menu Button - 紧挨着网站名称 */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden hover:bg-slate-100 dark:hover:bg-zinc-800 text-gray-600 dark:text-gray-300 ml-1" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Desktop Navigation - 调整间距和响应式 */}
        <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6 flex-1 justify-center">
          <SmartAnchorLink 
            href="#features" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.features")}
          </SmartAnchorLink>
          <SmartAnchorLink 
            href="#how-it-works" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.howItWorks")}
          </SmartAnchorLink>
          <SmartAnchorLink 
            href="#pricing" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.pricing")}
          </SmartAnchorLink>
          <Link href="/showcase" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.testimonials")}
          </Link>
          <SmartAnchorLink 
            href="#faq" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.faq")}
          </SmartAnchorLink>
          <SmartAnchorLink 
            href="#contact" 
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200 whitespace-nowrap"
          >
            {t("nav.contact")}
          </SmartAnchorLink>
        </nav>

        {/* Right Section - 始终显示的按钮区域 */}
        <div className="flex items-center space-x-0.5 xs:space-x-1 sm:space-x-2 lg:space-x-3">
          {/* 语言选择器 - 始终显示，在小屏幕上缩小 */}
          <div className="scale-75 sm:scale-90 lg:scale-100">
            <LanguageSelector />
          </div>
          <div className="scale-75 sm:scale-90 lg:scale-100">
            <ThemeToggle />
          </div>
          <AuthButton 
            className="professional-button bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-semibold shadow-lg text-xs sm:text-sm px-2 py-1.5 sm:px-3 sm:py-2 lg:px-4 lg:py-2.5 whitespace-nowrap min-w-[72px] sm:min-w-[90px] lg:min-w-[110px] truncate"
          />
        </div>
      </div>

      {/* Mobile Navigation - 在lg以下屏幕显示，绝对定位避免影响布局 */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-t border-slate-200/80 dark:border-zinc-700/80 shadow-lg">
          <nav className="container mx-auto py-4 space-y-2 px-4 lg:px-6">
            <SmartAnchorLink 
              href="#features" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.features")}
            </SmartAnchorLink>
            <SmartAnchorLink 
              href="#how-it-works" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.howItWorks")}
            </SmartAnchorLink>
            <SmartAnchorLink 
              href="#pricing" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.pricing")}
            </SmartAnchorLink>
            <Link href="/showcase" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.testimonials")}
            </Link>
            <SmartAnchorLink 
              href="#faq" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.faq")}
            </SmartAnchorLink>
            <SmartAnchorLink 
              href="#contact" 
              className="block py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-indigo-400 transition-colors duration-200"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("nav.contact")}
            </SmartAnchorLink>
          </nav>
        </div>
      )}
    </header>
  )
}

// 添加媒体查询样式以适配小屏幕
const styles = `
  @media (max-width: 640px) {
    .header-nav-link {
      font-size: 0.7rem !important; /* 进一步缩小字体 */
    }
    .header-button {
      font-size: 0.6rem !important; /* 进一步缩小按钮字体 */
      min-width: 40px !important; /* 进一步缩小按钮宽度 */
      padding: 3px 6px !important; /* 减少按钮内边距 */
    }
    .header-space {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    .container {
      padding-left: 0.5rem !important; /* 减少容器内边距 */
      padding-right: 0.5rem !important;
    }
    /* 新增样式：优化 Get Started 按钮 */
    .professional-button {
      font-size: 0.6rem !important; /* 进一步缩小字体 */
      min-width: 40px !important; /* 进一步缩小按钮宽度 */
      padding: 3px 6px !important; /* 减少按钮内边距 */
    }
  }
`;

// 将样式注入到页面中
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}